import { z } from 'zod';

import type { JibexAccountFormValues, JibexSaveBody } from './types';

/**
 * Mirrors apps/api/src/modules/delivery/http/delivery-account.dto.ts so the owner is told BEFORE sending what the
 * server would refuse. The server stays the authority — this only spares a round trip and a vague error.
 *
 * Messages are i18n KEYS (`merchant.delivery.account.form.*`), translated where they are rendered.
 */

const PRINTABLE_ASCII = /^[ -~]+$/;
const POSITIVE_INTEGER = /^[1-9]\d{0,15}$/;

export interface JibexSchemaContext {
  /** An account is already saved (so a blank secret means « keep it »). */
  readonly hasAccount: boolean;
  /** The saved client id: changing it needs a new secret (a secret belongs to the identity it was issued for). */
  readonly savedClientId: string | null;
}

export function jibexAccountFormSchema(
  context: JibexSchemaContext,
): z.ZodType<JibexAccountFormValues> {
  return z
    .object({
      clientId: z
        .string()
        .trim()
        .min(1, 'merchant.delivery.account.form.clientIdRequired')
        .max(200, 'merchant.delivery.account.form.clientIdTooLong')
        .regex(PRINTABLE_ASCII, 'merchant.delivery.account.form.charactersNotAllowed'),
      secret: z
        .string()
        .trim()
        .max(512, 'merchant.delivery.account.form.secretTooLong')
        .refine(
          (value) => value === '' || PRINTABLE_ASCII.test(value),
          'merchant.delivery.account.form.charactersNotAllowed',
        ),
      carrierStoreId: z
        .string()
        .trim()
        .refine(
          (value) => value === '' || POSITIVE_INTEGER.test(value),
          'merchant.delivery.account.form.storeIdInvalid',
        ),
    })
    .superRefine((values, ctx) => {
      const identityChanged = !context.hasAccount || values.clientId !== context.savedClientId;
      if (identityChanged && values.secret === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['secret'],
          message: 'merchant.delivery.account.form.secretRequired',
        });
      }
    });
}

/**
 * The request body. The secret key is OMITTED when the field is blank (the server then keeps the stored one) — never
 * sent as an empty string, never defaulted to anything.
 */
export function toSaveBody(values: JibexAccountFormValues): JibexSaveBody {
  const secret = values.secret.trim();
  const storeId = values.carrierStoreId.trim();
  return {
    clientId: values.clientId.trim(),
    ...(secret === '' ? {} : { secret }),
    carrierStoreId: storeId === '' ? null : Number(storeId),
  };
}
